import {useState, useEffect, useCallback, useMemo, SetStateAction} from 'react';

import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Dialog, {DialogProps} from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Iconify from 'src/components/iconify';
import {Upload} from 'src/components/upload';
import uuidv4 from 'src/utils/uuidv4';
import Checkbox from '@mui/material/Checkbox';
import {
  Paper,
  Typography,
  Grid,
  Tabs,
  Tab,
  Card,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Alert,
  Snackbar
} from '@mui/material';
import {Box, display} from '@mui/system';
import {Controller, useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {DesktopDatePicker} from '@mui/x-date-pickers';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {AdapterDateFns} from '@mui/x-date-pickers/AdapterDateFns';
import LockProperties from './LockProperties';
import {
  getLMSResultsData,
  getSearchForm,
  getSearchResultsData
} from '@shared/services/service-handlers/PrivateService';
import {getErrorMessage} from '@shared/utils/ErrorUtils';
import SnackBarConfig from '@components/snackbar/SnackbarConfig';
import {useAppContext} from '@context/AppContext';
import {enqueueSnackbar} from 'notistack';
import {jwtDecode} from 'jwt-decode';
import AuthUtils from 'src/utils/AuthUtils';
import EncryptUtils from 'src/utils/EncryptUtils';
import {LoadingButton} from '@mui/lab';
import {useLocation, useNavigate} from 'react-router-dom';
import React from 'react';
import {BASE_RECORD_ID, LMS_IS_ADMIN} from 'src/routes/BaseRoute';
import {useModulePreviewContext} from '@context/ModulePreviewContextProvider';
// ----------------------------------------------------------------------
interface FileToUpload {
  ids: string;
  fileData: string;
  fileName: string;
  fileType: string;
  fileError: string;
}
interface ModuleData {
  id: string;
  name: string;
  expanded: boolean;
  description?: string;
  duration?: string;
  type: string;
  sequence: number;
  module_lock_type_id?: number;
  locked_until_date?: string;
  Module_Completed?: boolean;
  IsUnlocked?: boolean;
  is_completed?: boolean;
  locked_linked_items?: {
    id: string;
    item_name: string;
    entity_completion_sub_type_id: number;
    module_link_id: number;
    module_id: number;
    type: 'file' | 'link' | 'content' | 'assignment' | 'onlinetest' | 'module';
  }[];
  files?: {
    id: string;
    name: string;
    ParentName: string;
    blob_file_extension?: string;
    fileexpanded: boolean;
    time?: string;
    score?: string;
    lock_type_id: number;
    locked_until_date?: string;
    type: string;
    module_id: number;
    sequence: number;
    ChildItems_Completed?: boolean;
    IsUnlocked?: boolean;
    is_completed?: boolean;
    additionalFiles?: {id: string; name: string}[];
    locked_linked_items?: {
      id: string;
      item_name: string;
      entity_completion_sub_type_id: number;
      module_link_id: number;
      module_id: number;
      type:
        | 'file'
        | 'link'
        | 'content'
        | 'assignment'
        | 'onlinetest'
        | 'module';
    }[];
  }[];
}
interface WrittenData {
  id: string;
  title: string;
  description: string;
  date: string;
  isFavorited: boolean;
  attachmentName?: FileToUpload[];
}
interface EditData {
  id?: string | null;
  operation?: string | null;
  name?: string;
  Duration?: number;
  Description?: string;
}
interface Props extends DialogProps {
  title?: string;
  uiFieldsdata: any;
  operation: string;
  //
  onCreate?: boolean;
  //
  folderName?: WrittenData;
  passedData?: any;
  onChangeFolderName?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  //
  open: boolean;
  onClose: VoidFunction;
  onSave?: (vals: WrittenData[]) => void; // Add your save logic here
  onEditSave?: (vals: WrittenData) => void; // Add your save logic here
}
interface BasicInfo {
  name: string;
  Duration: number;
  Description: string;
}
interface DropDownData {
  Name: string;
  Values: {label: string; value: number}[];
}
export default function CreateNewModule({
  title = 'Upload Files',
  uiFieldsdata,
  open,
  onClose,
  onCreate,
  folderName,
  passedData,
  onChangeFolderName,
  onSave,
  onEditSave,
  operation,
  ...other
}: Props) {
  const {
    appState: {applicationName}
  } = useAppContext();
  function useQuery() {
    const {search} = useLocation();
    return React.useMemo(() => new URLSearchParams(search), [search]);
  }
  const query = useQuery();
  const uniqueId = EncryptUtils.getDecryptedData(
    location?.search,
    BASE_RECORD_ID
  );
  const lmsisAdmin = EncryptUtils.getDecryptedData(
    location?.search,
    LMS_IS_ADMIN
  );
  const {ModulePreviewData, SetModulePreviewData} = useModulePreviewContext();
  const type = query.get('type');
  const section = query.get('section');
  const [basicInfo, setBasicInfo] = useState<BasicInfo>({
    name: passedData?.name || '',
    Duration: passedData?.Duration || 0,
    Description: passedData?.Description || ''
  });
  let userData: any;
  if (AuthUtils.getAuthToken()) {
    userData = jwtDecode(AuthUtils.getAuthToken() || '');
  }
  const [uiFields, setUiFields] = useState<any>(uiFieldsdata);
  const [isloaded, setIsLoaded] = useState(false);
  const [lockproploder, setLockproploader] = useState(false);
  const [saved, setSaved] = useState<boolean>(false);
  const [enableLockProperties, setEnableLockProperties] = useState(false);
  const [insertedId, setInsertedId] = useState<any>();
  const [dropDownData, setDropDownData] = useState<DropDownData[]>([]);
  const createValidationSchema = (questions: BasicInfo) =>
    yup.object().shape({
      name: yup.string().required('Name is required'),
      Duration: yup.number(),
      // .required('Duration is required')
      // .positive('Duration must be a positive number')
      Description: yup.string()
      // .required('Description is required')
    });

  // Memoize validation schema
  const validationSchema = useMemo(
    () => createValidationSchema(basicInfo),
    [basicInfo]
  );

  // Hook form methods
  const methods = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: basicInfo
  });

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: {errors}
  } = methods;
  const init = async () => {
    try {
      const res = await getSearchForm(
        applicationName,
        'module',
        'CreateModule'
      );
      setUiFields(res.searchForm);
    } catch (e) {
      const errMessage = getErrorMessage(e);
      enqueueSnackbar(errMessage, SnackBarConfig.getError());
    }
  };
  useEffect(() => {
    const fetchData = async () => {
      if (uiFieldsdata === undefined) {
        await init();
      } else {
        setUiFields(uiFieldsdata);
      }
      if (operation === 'INSERT') {
        setInsertedId(undefined);
      } else if (insertedId !== undefined) {
        setInsertedId(insertedId);
      } else {
        setInsertedId({
          data: [{id: passedData?.id, type: 'module'}]
        });
        setEnableLockProperties(true);
      }
    };

    fetchData();
  }, [open]);
  const handleSnackbarClose = () => {
    // setOpenSnackbar(false);
    setSaved(false);
  };

  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event: any, newValue: SetStateAction<number>) => {
    setActiveTab(newValue);
  };
  const generateOptionsList = (fields: any, data: any) => {
    const transformedData = data
      .map((item: {name: any; id: any}) => ({
        label: item.name,
        value: item.id
      }))
      .sort(
        (a: {sequence: number}, b: {sequence: number}) =>
          a.sequence - b.sequence
      );

    return transformedData;
  };
  const handleLegacyAppChange = async () => {
    const {sql_data, order_by, sort_by} = uiFields['lockOption'];
    try {
      setLockproploader(true);
      const data: any = {
        sql_data: sql_data,
        sort: order_by,
        sort_by: sort_by,
        isImportFiles: true
      };
      const encryptRequestData = EncryptUtils.encryptedData(data);
      const result = await getSearchResultsData(applicationName, 'LMS', {
        tk: encryptRequestData
      });
      setLockproploader(false);
      const new_options = generateOptionsList(
        [{generate_options: true}],
        result?.data
      );
      setDropDownData(prevData => {
        const existingEntry = prevData?.find(
          (entry: {Name: string}) => entry.Name === 'lockOption'
        );

        if (existingEntry) {
          // If the Name already exists, update its Values
          return [...prevData];
        }

        // If the Name is new, add it to the array
        return [...prevData, {Name: 'lockOption', Values: new_options}];
      });
      return new_options;
    } catch (e) {
      const errMessage = getErrorMessage(e);
      enqueueSnackbar(errMessage, SnackBarConfig.getError());
      setIsLoaded(false);
    }
  };
  // Handle form submission
  const onSubmit = async (datavalue: any) => {
    try {
      if (uiFields) {
        const {sql_data} = uiFields['create_sql'];
        let operations;
        if (insertedId && insertedId.data.length > 0) {
          operations = 'UPDATE';
          datavalue.id = insertedId?.data[0].id;
        } else {
          operations = operation;
        }
        datavalue.courseoverviewid = uniqueId;
        datavalue.isAdmin = lmsisAdmin === 'True';
        datavalue.page = 1;
        setIsLoaded(true);
        const data: any = {
          sql_data: sql_data,
          data: datavalue,
          operation: operations
        };
        const encryptRequestData = EncryptUtils.encryptedData(data);
        const result = await getLMSResultsData(applicationName, 'LMS', {
          tk: encryptRequestData
        });
        // if (operation === 'INSERT') {
          setInsertedId({data: [{...result.data[0][0], type: 'module'}]});
        // } else {
        //   setInsertedId({data: [{...result.data[0], type: 'module'}]});
        // }
        const processModulesData = (data: ModuleData[]) => {
          return data.map(module => ({
            ...module,
            files:
              module.files?.map(file => ({
                ...file,
                locked_linked_items:
                  typeof file.locked_linked_items === 'string'
                    ? JSON.parse(file.locked_linked_items) // 🛠️ Parse if string
                    : file.locked_linked_items || [], // Keep array or default to empty
                additionalFiles:
                  typeof file.additionalFiles === 'string'
                    ? JSON.parse(file.additionalFiles) // 🛠️ Parse if string
                    : file.additionalFiles || [] // Keep array or default to empty
              })) || []
          }));
        };
        const syncExpandedStates = (
          transformed: ModuleData[],
          existing: ModuleData[]
        ) => {
          return transformed.map(module => {
            const matchingModule = existing.find(e => e.id === module.id);

            if (!matchingModule) return module;

            // Sync the `expanded` flag
            const updatedModule = {
              ...module,
              expanded: matchingModule.expanded || false,
              files:
                module?.files &&
                module?.files.map(file => {
                  const matchingFile =
                    matchingModule?.files &&
                    matchingModule.files.find(f => f.id === file.id);
                  return {
                    ...file,
                    fileexpanded: matchingFile?.fileexpanded || false
                  };
                })
            };

            return updatedModule;
          });
        };

        // Handling API result
        if (result.data[1]) {
          const transformData = await processModulesData(result.data[1]);
          const updatedTransformed = syncExpandedStates(
            transformData,
            ModulePreviewData
          );
          await SetModulePreviewData(updatedTransformed);
        }
        // setValue('name',result)

        // await handleLegacyAppChange();

        setIsLoaded(false);
        setSaved(true);
        setEnableLockProperties(true);
        setActiveTab(1);
      }
    } catch (e) {
      const errMessage = getErrorMessage(e);
      enqueueSnackbar(errMessage, SnackBarConfig.getError());
      setIsLoaded(false);
    }
  };

  return (
    <Dialog fullWidth maxWidth="lg" open={open} {...other}>
      <DialogTitle sx={{p: theme => theme.spacing(3, 3, 2, 3)}}>
        <h3 style={{marginRight: '-0.5vh'}}> {title}</h3>

        <Stack
          direction="row"
          alignItems="center"
          sx={{
            top: 8,
            right: 8,
            position: 'absolute'
          }}>
          <IconButton
            aria-label="close"
            onClick={() => {
              setActiveTab(0);
              reset();
              onClose();
            }}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{pt: 1, pb: 0, border: 'none'}}>
        <Card
          elevation={3}
          sx={{
            p: 3,
            minHeight: '70vh',
            border: '1px solid #e0e0e0', // Add border to all sides
            marginBottom: 3 // Add margin at the bottom
          }}>
          <Grid container spacing={2} sx={{minHeight: '68vh'}}>
            {/* Tabs and Content */}

            {/* Tab Content */}
            <Grid item xs={12} md={10}>
              {activeTab === 0 && (
                <form
                  onSubmit={handleSubmit(onSubmit)}
                  style={{height: '100%'}}>
                  <Grid container spacing={2}>
                    {/* Name Field */}
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="name"
                        control={control}
                        render={({field}) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Name"
                            variant="outlined"
                            error={!!errors.name}
                            helperText={errors.name?.message}
                          />
                        )}
                      />
                    </Grid>
                    {/* Duration Field */}
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="Duration"
                        control={control}
                        defaultValue={undefined}
                        render={({field}) => (
                          <TextField
                            {...field}
                            // defaultValue=''
                            fullWidth
                            label="Duration (minutes)"
                            variant="outlined"
                            type="number"
                            error={!!errors.Duration}
                            helperText={errors.Duration?.message}
                          />
                        )}
                      />
                    </Grid>
                    {/* Description Field */}
                    <Grid item xs={12}>
                      <Controller
                        name="Description"
                        control={control}
                        render={({field}) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Description"
                            multiline
                            rows={4}
                            variant="outlined"
                            error={!!errors.Description}
                            helperText={errors.Description?.message}
                          />
                        )}
                      />
                    </Grid>
                    {/* Save Button */}
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: '3vh',
                        left: '2vh'
                      }}>
                      <LoadingButton
                        variant="contained"
                        color="primary"
                        type="submit"
                        loading={isloaded}>
                        Save
                      </LoadingButton>
                    </Box>
                  </Grid>
                </form>
              )}

              {/* Form for Lock Properties */}
              {activeTab === 1 && (
                <LockProperties
                  uiFields={uiFields}
                  insertdata={insertedId}
                  isloader={lockproploder}
                  // dropdowndata={dropDownData}
                />
              )}
            </Grid>
            <Grid item xs={12} md={2}>
              <Box>
                <Tabs
                  value={activeTab}
                  onChange={handleTabChange}
                  orientation="vertical"
                  indicatorColor="primary"
                  textColor="primary"
                  variant="fullWidth"
                  TabIndicatorProps={{
                    sx: {
                      left: 0, // Align the indicator to the left
                      width: 4 // Make the indicator 4px wide
                    }
                  }}
                  sx={{display: 'inline-grid'}}>
                  <Tab
                    label="Basic Info"
                    sx={{
                      marginLeft: '1vh',
                      minWidth: '20vh',
                      justifyContent: 'flex-start',
                      backgroundColor: activeTab === 0 ? 'white' : 'transparent'
                    }}
                  />
                  <Tab
                    label="Lock Properties"
                    disabled={!enableLockProperties}
                    sx={{
                      marginLeft: '1vh',
                      minWidth: '20vh',
                      justifyContent: 'flex-start',
                      backgroundColor:
                        activeTab === 1 ? 'white' : 'transparent',
                      color: !enableLockProperties
                        ? '#b6babd !important'
                        : '#212B36 !important'
                    }}
                  />
                </Tabs>
              </Box>
            </Grid>
          </Grid>
        </Card>
      </DialogContent>
      <Snackbar
        open={saved}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{vertical: 'top', horizontal: 'center'}}>
        <Alert
          onClose={handleSnackbarClose}
          severity="success"
          sx={{width: '40vw'}}>
          New Module is Created
        </Alert>
      </Snackbar>
    </Dialog>
  );
}
